import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Row, Tab, Tabs } from "react-bootstrap";
import { push } from "store/actions/router";
import { get, includes, map } from "lodash";

import { getUser } from "actions/adminActions";
import { individualYellowpathStates } from "statics/states";

import UserInfo from "./UserInfo";
import UserTransactions from "./UserTransactions";
import EmployerLinks from "pages/dashboards/adminDashboard/users/EmployerLinks";
import UserAccounts from "pages/dashboards/adminDashboard/users/UserAccounts";
import UserRollovers from "pages/dashboards/adminDashboard/users/UserRollovers";
import ContributionAbility from "pages/dashboards/adminDashboard/users/ContributionAbility";
import UserYellowPath from "pages/dashboards/adminDashboard/users/UserYellowPath";
import EntityLimits from "../EntityLimits";
import { isSuperIconAdmin } from "store/selectors/admin";
import type { RootState } from "types/state";

interface OwnProps {
  // Injected by the User_WithLocation wrapper below, not by connect.
  routeName?: string;
  userId?: string;
  // Declared on the original propTypes spec but never supplied by the
  // wrapper or connect, and never read in the class body. Kept optional.
  error?: string;
  isFetching?: boolean;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  return {
    userId: ownProps.userId,
    isSuperAdmin: isSuperIconAdmin(state),
  };
};

const mapDispatchToProps = {
  push,
  getUser,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  tab: string;
  userState: string | null;
}

class User extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      tab: this._calcActiveTab(props.routeName),
      userState: null,
    };
  }

  async componentDidMount() {
    // getUser resolves to the dispatched action; the SUCCESS branch carries
    // the getUserDetails payload under `data`. `get` reads `data.user.state`.
    const { data } = (await this.props.getUser(
      this.props.client as ApolloClient<object>,
      // `userId` comes from the optional route param; the original JS passed it
      // through to getUser unchanged (undefined when absent). Cast at the
      // boundary to match getUser's string | number param without altering the
      // runtime value.
      this.props.userId as string
    )) as { data?: unknown };
    this.setState({
      userState: get(data, "user.state") as unknown as string | null,
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.routeName !== this.props.routeName) {
      this.setState({ tab: this._calcActiveTab(this.props.routeName) });
    }
  }

  _isYellowpath = () => {
    return includes(individualYellowpathStates, this.state.userState);
  };

  _getTabs = () => {
    let allTabs = [
      {
        label: "Info",
        id: "info",
      },
      {
        label: "Transactions",
        id: "transactions",
      },
      {
        label: "Employers",
        id: "employers",
      },
      {
        label: "Accounts",
        id: "accounts",
      },
      {
        label: "Rollovers",
        id: "rollovers",
      },
      {
        label: "Contribution Ability",
        id: "contribution_ability",
      },
    ];

    if (this._isYellowpath()) {
      allTabs.push({
        label: "Yellowpath",
        id: "yellowpath",
      });
    }

    if (this.props.isSuperAdmin) {
      allTabs.push({
        label: "Limits",
        id: "limits",
      });
    }

    return allTabs;
  };

  _tabUpdate(value: { k: string | null }) {
    this.setState({ tab: value.k as string });
    this.props.push(`/users/${this.props.userId}/${value.k}`);
  }

  _controlledTabs() {
    const tabs = this._getTabs();
    return (
      <Tabs
        activeKey={this.state.tab}
        id="tab-user"
        onSelect={(k) => this._tabUpdate({ k })}
      >
        {map(tabs, (tab) => {
          return <Tab key={tab.id} eventKey={tab.id} title={tab.label} />;
        })}
      </Tabs>
    );
  }

  _calcActiveTab = (routeName?: string) => {
    switch (routeName) {
      case `/users/${this.props.userId}/info`:
        return "info";
      case `/users/${this.props.userId}/transactions`:
        return "transactions";
      case `/users/${this.props.userId}/employers`:
        return "employers";
      case `/users/${this.props.userId}/accounts`:
        return "accounts";
      case `/users/${this.props.userId}/contribution_ability`:
        return "contribution_ability";
      case `/users/${this.props.userId}/rollovers`:
        return "rollovers";
      case `/users/${this.props.userId}/yellowpath`:
        return "yellowpath";
      case `/users/${this.props.userId}/limits`:
        return "limits";

      default:
        return "info";
    }
  };

  _getCardContent = () => {
    switch (this.state.tab) {
      case "info":
        return <UserInfo userId={this.props.userId} />;
      case "transactions":
        return <UserTransactions userId={this.props.userId} />;
      case "employers":
        return <EmployerLinks userId={this.props.userId} />;
      case "accounts":
        return <UserAccounts userId={this.props.userId} />;
      case "rollovers":
        return <UserRollovers userId={this.props.userId} />;
      case "contribution_ability":
        return <ContributionAbility userId={this.props.userId} />;
      case "yellowpath":
        return <UserYellowPath userId={this.props.userId} />;
      case "limits":
        return <EntityLimits entityId={this.props.userId} entityType="USER" />;
    }
  };

  render() {
    return (
      <div className="mega-container" id={"admin-user-page"}>
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">User Info</h1>

            <div className="main-content">
              <Row>
                <Col>
                  <div className="card-tab">{this._controlledTabs()}</div>
                  <Card>{this._getCardContent()}</Card>
                </Col>
              </Row>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

const User_WithLocation = (props: Props) => {
  const { pathname: routeName } = useLocation();
  const { id: userId } = useParams();
  return <User {...props} routeName={routeName} userId={userId} />;
};

export default connector(withApollo<Props>(User_WithLocation));
